import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";
import { Card, Container, Header, Icon, Segment } from "semantic-ui-react";
import { useTranslation } from "react-i18next";
import * as corsiApi from "../../api/corsiApi";
import * as appActions from "../../redux/actions/appActions";
import * as cartActions from "../../redux/actions/cartActions";
import SchedaCorsoCard from "../collettive/SchedaCorsoCard";
import RedirectWithTrack from "../common/RedirectWithTrack";
import DettaglioOfferta from "./DettaglioOfferta";

/**
 * Sezione "Offerte / Pacchetti" in fondo all'area lezioni private.
 *
 * Montata sia da PrivateMain sia da PrivateNewMain; renderizza il proprio Container, così le due
 * varianti restano identiche a livello di layout. Se la scuola non ha offerte non renderizza nulla.
 *
 * Le sessioni vengono caricate in stato locale e NON tramite corsiActions.loadSessioni: quello
 * scrive su `state.corsi.sessioni`, uno slot globale condiviso col flusso di dettaglio dei corsi
 * collettivi, che verrebbe sovrascritto.
 */
function OffersSection({
  appData,
  datiScuola,
  cart,
  setCorsoDettagli,
  setPrefsCorso,
  setReferrer,
}) {
  const [offerte, setOfferte] = useState([]);
  const [idCorsoSelezionato, setIdCorsoSelezionato] = useState(null);
  const [corsoSelezionato, setCorsoSelezionato] = useState(null);
  const [sessioniCorso, setSessioniCorso] = useState(null);
  const [redirectToAllievi, setRedirectToAllievi] = useState(false);

  const { t, i18n } = useTranslation([datiScuola?.customTranslationNamespace]);

  useEffect(() => {
    i18n.emit("languageChanged");
  }, [datiScuola?.customTranslationNamespace]);

  useEffect(() => {
    if (!datiScuola?.id) return;

    let cancelled = false;
    (async () => {
      const res = await corsiApi.getListaOfferte(
        datiScuola.id,
        moment().format("YYYY-MM-DD"),
        datiScuola.idLinguaInUso,
        appData?.agenzia?.idAgenzia ?? 0
      );
      if (!cancelled) setOfferte(Array.isArray(res) ? res : []);
    })();

    return () => {
      cancelled = true;
    };
  }, [datiScuola?.id, datiScuola?.idLinguaInUso, appData?.agenzia?.idAgenzia]);

  async function handleCorsoSelezionato(idCorso) {
    if (idCorsoSelezionato === idCorso) {
      setIdCorsoSelezionato(null);
      setCorsoSelezionato(null);
      setSessioniCorso(null);
      return;
    }

    const corso = offerte.find((x) => x.idCorso === idCorso);
    if (!corso) return;

    setIdCorsoSelezionato(idCorso);
    setCorsoSelezionato(corso);
    setSessioniCorso(null);

    const res = await corsiApi.getListaSessioni(
      datiScuola.id,
      corso.id,
      moment().format("YYYY-MM-DD"),
      datiScuola.idLinguaInUso,
      appData?.agenzia?.idAgenzia ?? -1
    );
    setSessioniCorso(res);
  }

  function handleAvanti(
    primaData,
    giorni,
    numSessioni,
    allievi,
    prezzo,
    prezzoPag,
    prezzoListino,
    listaDate,
    scontoPromozione,
    scontoAgenzia,
    prezzoListinoRiferimento
  ) {
    if (cart.righeCollettive.find((x) => x.roomingList === true) !== undefined) {
      alert(
        "Nel carrello è già presente un corso con rooming list, non è possibile aggiungere un altro corso"
      );
      return;
    }

    setCorsoDettagli(corsoSelezionato);
    setPrefsCorso({
      primaData: primaData,
      numGiorni: giorni,
      numSessioni: numSessioni,
      numAllievi: allievi,
      prezzo: prezzo,
      prezzoPag: prezzoPag,
      prezzoListino: prezzoListino,
      prezzoListinoRiferimento: prezzoListinoRiferimento,
      listaDate: listaDate,
      scontoPromozione: scontoPromozione || 0,
      scontoAgenzia: scontoAgenzia || 0,
      // Il prezzo di un pacchetto è già il totale della comitiva.
      prezzoTotale: true,
      origine: "private",
    });
    setReferrer("private");
    setRedirectToAllievi(true);
  }

  if (offerte.length === 0) return null;

  return (
    <Container>
      {redirectToAllievi && (
        <RedirectWithTrack
          to={`/${appData.lang}/${appData.slug}/collettive/allievi`}
          push
        />
      )}
      <Segment basic style={{ marginBottom: "30px" }}>
        <Header as="h2">
          <Icon name="gift"></Icon>
          <Header.Content>{t("offerte_pacchetti")}</Header.Content>
        </Header>

        <Card.Group centered>
          {offerte.map((corso) => (
            <SchedaCorsoCard
              key={corso.idCorso}
              t={t}
              codScuola={datiScuola.codicePrimario || datiScuola.codiceScuola}
              corso={corso}
              isVisible={true}
              onSelect={handleCorsoSelezionato}
            />
          ))}
        </Card.Group>

        {corsoSelezionato && sessioniCorso && (
          <DettaglioOfferta
            corso={corsoSelezionato}
            sessioniCorso={sessioniCorso}
            datiScuola={datiScuola}
            appData={appData}
            onAvantiClick={handleAvanti}
          />
        )}
      </Segment>
    </Container>
  );
}

OffersSection.propTypes = {
  appData: PropTypes.object.isRequired,
  datiScuola: PropTypes.object.isRequired,
  cart: PropTypes.object.isRequired,
  setCorsoDettagli: PropTypes.func.isRequired,
  setPrefsCorso: PropTypes.func.isRequired,
  setReferrer: PropTypes.func.isRequired,
};

// datiScuola arriva da scuolaReducer, NON da scuolaNewReducer: PrivateNewMain mappa quest'ultimo,
// quindi la sezione non può ereditarlo come prop.
function mapStateToProps(state) {
  return {
    appData: state.appReducer,
    datiScuola: state.scuolaReducer,
    cart: state.cartReducer,
  };
}

const mapDispatchToProps = {
  setCorsoDettagli: appActions.setCorsoDettagli,
  setPrefsCorso: appActions.setPrefsCorso,
  setReferrer: cartActions.setReferrer,
};

export default connect(mapStateToProps, mapDispatchToProps)(OffersSection);
